import type {
  DashboardOverview,
  ActivityDayPoint,
  EngagementComparison,
  TimeWindow,
} from './types';
import { TIME_WINDOW_DAYS } from './types';

/**
 * Sélectionne une valeur KPI de l'overview selon la fenêtre temporelle demandée.
 * `season` retourne la valeur `all_time`.
 */
export function selectWindowedKpi(
  overview: DashboardOverview | null,
  base: 'trades' | 'signings' | 'drops' | 'ltir_placed' | 'ltir_reactivated',
  window: TimeWindow
): number {
  if (!overview) return 0;
  if (base === 'ltir_placed' || base === 'ltir_reactivated') {
    const suffix =
      window === 'season' ? 'all_time' : window === '7d' ? 'last_7d' : window === '30d' ? 'last_30d' : 'last_90d';
    return overview[`${base}_${suffix}`] ?? 0;
  }
  if (base === 'trades') {
    if (window === 'season') return overview.trades_all_time;
    if (window === '7d') return overview.trades_last_7d;
    if (window === '30d') return overview.trades_last_30d;
    return overview.trades_last_90d;
  }
  if (base === 'signings') {
    if (window === 'season') return overview.signings_all_time;
    if (window === '7d') return overview.signings_last_7d;
    if (window === '30d') return overview.signings_last_30d;
    return overview.signings_last_90d;
  }
  if (window === 'season') return overview.drops_all_time;
  if (window === '7d') return overview.drops_last_7d;
  if (window === '30d') return overview.drops_last_30d;
  return overview.drops_last_90d;
}

export function selectNewJoinersKpi(
  overview: DashboardOverview | null,
  window: TimeWindow
): number {
  if (!overview) return 0;
  // Pas de champ new_joiners_all_time : sur "season" on retombe sur total_poolers.
  if (window === 'season') return overview.total_poolers ?? 0;
  if (window === '7d') return overview.new_joiners_last_7d;
  if (window === '30d') return overview.new_joiners_last_30d;
  return overview.new_joiners_last_90d;
}

/**
 * Calcule le total agrégé d'une métrique sur la série quotidienne. Utile pour
 * vérifier la cohérence overview vs timeseries dans les tests.
 */
export function sumSeries(
  points: ActivityDayPoint[],
  key: 'trades' | 'signings' | 'drops' | 'ltir_placed' | 'ltir_reactivated' | 'new_joiners'
): number {
  return points.reduce((acc, p) => acc + (p[key] ?? 0), 0);
}

/**
 * Pourcentage d'équipes complétées (0-100). Retourne 0 quand aucun pooler.
 */
export function completionRate(overview: DashboardOverview | null): number {
  if (!overview) return 0;
  const total = overview.total_poolers ?? 0;
  if (total === 0) return 0;
  return Math.round((100 * (overview.completed_builds ?? 0)) / total);
}

/**
 * Retourne le nombre de jours pour une fenêtre temporelle donnée (utilisé pour
 * paramétrer la RPC timeseries). `season` renvoie une valeur "large" (400)
 * plafonnée côté SQL.
 */
export function windowDays(window: TimeWindow): number {
  const days = TIME_WINDOW_DAYS[window];
  return days ?? 400;
}

/** Format court "DD/MM" pour l'axe X du graphique. */
export function formatShortDate(iso: string): string {
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00Z' : ''));
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

export interface MetricComparison {
  current: number;
  previous: number;
  difference: number;
  percentChange: number | null;
}

export function compareMetric(current: number, previous: number): MetricComparison {
  return {
    current,
    previous,
    difference: current - previous,
    percentChange: previous === 0 ? null : ((current - previous) / previous) * 100,
  };
}

/** Score provisoire = poolers distincts signataires / poolers actuels × 100. */
export function engagementScore(
  distinctSigners: number,
  totalPoolers: number
): number {
  if (totalPoolers <= 0) return 0;
  const signers = Math.max(0, Math.min(distinctSigners, totalPoolers));
  return Math.round((100 * signers) / totalPoolers);
}

export function scoreComparison(
  comparison: EngagementComparison | null,
  totalPoolers: number
): MetricComparison | null {
  if (!comparison) return null;
  return compareMetric(
    engagementScore(comparison.poolers_signing_current, totalPoolers),
    engagementScore(comparison.poolers_signing_previous, totalPoolers)
  );
}

export type ActivityChartPoint = ActivityDayPoint & {
  total_activity: number;
  moving_average_7d: number | null;
};

/** Total quotidien des actions de poolers (échanges, signatures, libérations, LTIR). */
export function dailyActivityTotal(point: ActivityDayPoint): number {
  return (
    point.trades +
    point.signings +
    point.drops +
    (point.ltir_placed ?? 0) +
    (point.ltir_reactivated ?? 0)
  );
}

/** Preserve each observed daily count and add a seven-day trailing average. */
export function addActivityTrend(points: ActivityDayPoint[]): ActivityChartPoint[] {
  return points.map((point, index) => {
    const total_activity = dailyActivityTotal(point);
    const start = Math.max(0, index - 6);
    const values = points.slice(start, index + 1).map(dailyActivityTotal);
    return {
      ...point,
      total_activity,
      moving_average_7d:
        values.length === 7
          ? values.reduce((sum, value) => sum + value, 0) / values.length
          : null,
    };
  });
}

export function detectExceptionalActivity(
  points: ActivityDayPoint[]
): { current: number; baselineAverage: number } | null {
  if (points.length < 8) return null;
  const previousDays = points.slice(-8, -1);
  const current = points[points.length - 1];
  const baselineAverage =
    previousDays.reduce(
      (sum, point) => sum + point.signings,
      0
    ) / previousDays.length;
  return current.signings >= 3 && current.signings >= baselineAverage * 2
    ? { current: current.signings, baselineAverage }
    : null;
}

export function detectPoolGrowth(
  current: number,
  previous: number
): boolean {
  return previous > 0
    ? current >= previous * 2 && current - previous >= 3
    : current >= 3;
}

export function detectActivityDecline(
  current: number,
  previous: number
): boolean {
  return previous > 0 && current < previous * 0.5;
}

export function contentPriorityScore(input: {
  volume: number;
  absoluteIncrease?: number;
  percentIncrease?: number | null;
  exceptional?: boolean;
  eventTime?: string | null;
  now?: number;
  /** Part des équipes du pool qui ont le joueur (0-100), si connue. */
  ownershipShare?: number | null;
}): number {
  const volumeScore = Math.min(30, Math.max(0, input.volume) * 3);
  const absoluteScore = Math.min(20, Math.max(0, input.absoluteIncrease ?? 0) * 2);
  const percentScore = Math.min(
    30,
    Math.max(0, input.percentIncrease ?? 0) * 0.3
  );
  const exceptionalScore = input.exceptional ? 10 : 0;
  const ownershipScore = Math.min(40, Math.max(0, input.ownershipShare ?? 0) * 0.8);
  let freshnessScore = 0;
  if (input.eventTime) {
    const elapsed = (input.now ?? Date.now()) - new Date(input.eventTime).getTime();
    if (Number.isFinite(elapsed) && elapsed >= 0) {
      freshnessScore = elapsed <= 24 * 60 * 60 * 1000 ? 10 : elapsed <= 7 * 24 * 60 * 60 * 1000 ? 5 : 0;
    }
  }
  return Math.round(
    Math.min(100, volumeScore + absoluteScore + percentScore + exceptionalScore + freshnessScore + ownershipScore)
  );
}

export type TrendMetric = 'signings' | 'new_joiners' | 'roster_moves' | 'trades';

/** Valeur quotidienne d'une métrique ; `roster_moves` = libérations + mouvements LTIR. */
export function dailyMetricValue(point: ActivityDayPoint, metric: TrendMetric): number {
  if (metric === 'roster_moves') {
    return point.drops + (point.ltir_placed ?? 0) + (point.ltir_reactivated ?? 0);
  }
  return point[metric] ?? 0;
}

export interface TrendPoint {
  day: string;
  value: number;
  /** Moyenne des 7 derniers jours, null tant que 7 jours ne sont pas disponibles. */
  average: number | null;
}

/**
 * Série d'une métrique pour les `days` derniers jours. La moyenne mobile est
 * calculée sur tout l'historique reçu pour que les premiers jours de la
 * fenêtre aient déjà une moyenne.
 */
export function buildTrendSeries(
  history: ActivityDayPoint[],
  metric: TrendMetric,
  days: number
): TrendPoint[] {
  const values = history.map((point) => dailyMetricValue(point, metric));
  return history
    .map((point, index) => {
      const slice = values.slice(Math.max(0, index - 6), index + 1);
      return {
        day: point.day,
        value: values[index],
        average:
          slice.length === 7
            ? Math.round((slice.reduce((sum, v) => sum + v, 0) / 7) * 10) / 10
            : null,
      };
    })
    .slice(-days);
}
